import React, { Component, lazy, Suspense, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { P } from '../../constants/permissions';
import { assistantService } from '../../services/api';
import { isSuperAdminUser } from '../../utils/platformRoles';
import { isLinaDockVisiblePath } from './linaDockVisibility';

const LinaAvatar = lazy(() => import('./LinaAvatar'));

class DockGuard extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) return this.props.fallback;
    return this.props.children;
  }
}

export default function LinaDock() {
  const { t } = useTranslation(['assistant']);
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { pathname } = useLocation();
  const [insightShort, setInsightShort] = useState('');
  const visible =
    Boolean(user) &&
    !isSuperAdminUser(user) &&
    hasPermission(P.DASHBOARD_VIEW) &&
    isLinaDockVisiblePath(pathname);

  useEffect(() => {
    if (!visible) return undefined;
    document.documentElement.dataset.linaDock = 'on';
    return () => {
      delete document.documentElement.dataset.linaDock;
    };
  }, [visible]);

  useEffect(() => {
    if (!visible) return undefined;
    let active = true;
    assistantService
      .insight()
      .then(({ data }) => {
        if (active) setInsightShort(data?.text_short || '');
      })
      .catch(() => {
        if (active) setInsightShort('');
      });
    return () => {
      active = false;
    };
  }, [visible]);

  if (!visible) return null;

  const label = t('assistant:talkToLina');
  const bubble = insightShort || t('assistant:dockAsk');

  return (
    <Link to="/assistant" className="lina-dock" aria-label={label} title={insightShort || label}>
      <span className={`lina-dock-bubble${insightShort ? ' has-insight' : ''}`}>{bubble}</span>
      <DockGuard fallback={<span className="lina-dock-fallback">L</span>}>
        <Suspense fallback={<span className="lina-dock-fallback">L</span>}>
          <LinaAvatar mood="greet" variant="dock" label={t('assistant:linaName')} />
        </Suspense>
      </DockGuard>
    </Link>
  );
}
